// Leaderboard: the daily (today's edition or the last seven, with the score spread) or Slow Burn
// (today's runs or each player's best ever, by points then row). Every row loads as you scroll, 50 at
// a time, and your row stays on screen, with a pill that takes you to your place. A member's row
// shows their badge and opens their profile.
import { Fragment, memo, useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, useSearchParams } from 'react-router'
import type { Leaderboard, LeaderboardEntry, LeaderboardScope, SlowBurnBoard, SlowBurnEntry, SlowBurnScope } from '@wbwl/contract'
import { NameBadge } from '../components/Badge.tsx'
import { Histogram } from '../components/game-ui.tsx'
import { Avatar, ErrorNote, Loading, TabBar } from '../components/shell.tsx'
import { DepthHistogram, SlowBurnMark } from '../components/slow-burn-ui.tsx'
import { api } from '../lib/api.ts'
import { useEdition, useLeaderboard, useSlowBurn, useSlowBurnBoard } from '../lib/data.ts'
import { fmt } from '../lib/game.ts'

export default function LeaderboardPage() {
  const [params, setParams] = useSearchParams()
  const slowBurn = params.get('mode') === 'slow-burn'
  const setMode = (sb: boolean) => setParams(() => (sb ? new URLSearchParams({ mode: 'slow-burn' }) : new URLSearchParams()))
  return (
    <div className="page leaderboard">
      <div className="lb-head">
        <h1 className="h-serif">Leaderboard</h1>
        <nav className="lb-modes" role="tablist">
          <button type="button" role="tab" aria-selected={!slowBurn} className={slowBurn ? '' : 'on'} onClick={() => setMode(false)}>The daily</button>
          <button type="button" role="tab" aria-selected={slowBurn} className={slowBurn ? 'on' : ''} onClick={() => setMode(true)}><SlowBurnMark />Slow Burn</button>
        </nav>
      </div>
      {slowBurn ? <SlowBurnBoardView /> : <DailyBoard />}
      <TabBar />
    </div>
  )
}

function DailyBoard() {
  const [params, setParams] = useSearchParams()
  const scope: LeaderboardScope = params.get('scope') === 'week' ? 'week' : 'today'
  const today = useEdition('today').data
  const n = params.get('n') ? Number(params.get('n')) : today?.number ?? null
  const lb = useLeaderboard(n, scope)
  const board = lb.data
  const set = (s: LeaderboardScope) => setParams((p) => { const q = new URLSearchParams(p); if (s === 'week') q.set('scope', 'week'); else q.delete('scope'); return q })

  return (
    <>
      <div className="lb-scope">
        <div className="seg" role="tablist">
          <button type="button" role="tab" aria-selected={scope === 'today'} className={scope === 'today' ? 'on' : ''} onClick={() => set('today')}>{n && today && n !== today.number ? `No. ${n}` : 'Today'}</button>
          <button type="button" role="tab" aria-selected={scope === 'week'} className={scope === 'week' ? 'on' : ''} onClick={() => set('week')}>This week</button>
        </div>
      </div>
      {lb.error ? <ErrorNote error={lb.error} retry={() => lb.reload()} /> : !board ? <Loading label="Tallying scores…" /> : (
        <>
          <div className="card lb-summary">
            <div className="lb-sum-head"><b>{scope === 'today' ? `No. ${board.edition}` : `Nos. ${Math.max(0, board.edition - 6)}–${board.edition}`} · {fmt(board.players)} player{board.players === 1 ? '' : 's'}</b><span className="eyebrow">{board.best !== null ? `best ${fmt(board.best)}` : ''}</span></div>
            {scope === 'today' && board.histogram.length > 0 && <Histogram bins={board.histogram} you={board.me?.total ?? null} height={40} label={false} />}
            {board.average !== null && <div className="faint mono small">average {fmt(board.average)}</div>}
          </div>
          {board.entries.length === 0 ? (
            <p className="muted empty-note">Nobody has finished yet. Be the first on the board.</p>
          ) : (
            <BoardList first={board} load={(offset, limit) => api.get<Leaderboard>(`/leaderboards/${board.edition}?scope=${scope}&offset=${offset}&limit=${limit}`)} row={(e) => <Row e={e} />} />
          )}
        </>
      )}
    </>
  )
}

/** Rows a scroll loads at a time, and at most in one request (the API's cap). */
const PAGE = 50
const MAX_PAGE = 200

interface BoardPage<E> {
  readonly entries: readonly E[]
  readonly players: number
  readonly me: E | null
  readonly meRow: number | null
}

/**
 * A board's rows, loading the next page as the end of the list comes near. Your row is in its place
 * once its page has loaded; until then it waits after a gap at the end of what's loaded, and its
 * pill loads the pages up to it and then scrolls there.
 */
function BoardList<E extends { readonly playerId: string; readonly isMe: boolean }>({ first, load, row }: {
  first: BoardPage<E>; load(offset: number, limit: number): Promise<BoardPage<E>>; row(e: E): ReactNode
}) {
  const [rows, setRows] = useState<readonly E[]>(first.entries)
  const [loading, setLoading] = useState(false)
  const [jumping, setJumping] = useState(false)
  const end = useRef<HTMLDivElement>(null)
  const list = useRef<HTMLDivElement>(null)
  const busy = useRef(false)
  const done = rows.length >= first.players
  // A new board (another scope or edition, or a refetch) starts again from its first page.
  useEffect(() => { setRows(first.entries) }, [first])

  /** Loads rows up to `through` (row number), or the next page; one request at a time. */
  const more = useCallback(async (through?: number) => {
    if (busy.current) return
    busy.current = true
    setLoading(true)
    try {
      let have = rows
      do {
        const want = through === undefined ? PAGE : Math.min(MAX_PAGE, through - have.length + 10)
        const page = await load(have.length, want)
        if (!page.entries.length) break
        have = [...have, ...page.entries]
      } while (through !== undefined && have.length < through)
      setRows(have)
    } catch {
      // The sentinel asks again the next time it comes into view.
    } finally {
      busy.current = false
      setLoading(false)
    }
  }, [rows, load])

  useEffect(() => {
    const el = end.current
    if (!el || done) return
    const io = new IntersectionObserver((seen) => { if (seen.some((s) => s.isIntersecting)) void more() }, { rootMargin: '600px 0px' })
    io.observe(el)
    return () => io.disconnect()
  }, [done, more])

  // After loading the pages up to your row, scroll to it in its place.
  const meLoaded = rows.some((e) => e.isMe)
  useEffect(() => {
    if (!jumping || !meLoaded) return
    setJumping(false)
    requestAnimationFrame(() => { const pin = list.current?.querySelector<HTMLElement>('.lb-pin'); if (pin) scrollToPlace(pin) })
  }, [jumping, meLoaded])

  const toMe = first.me && !meLoaded && first.meRow !== null
    ? async () => { setJumping(true); await more(first.meRow!) }
    : undefined
  return (
    <div className="lb-list" ref={list}>
      {rows.map((e) => (e.isMe ? <MyRow key={e.playerId}>{row(e)}</MyRow> : <Fragment key={e.playerId}>{row(e)}</Fragment>))}
      <div ref={end} className={`lb-more${loading ? ' on' : ''}`} aria-hidden={!loading}>{loading ? 'Loading more…' : ''}</div>
      {first.me && !meLoaded && (
        <><div className="lb-gap" aria-hidden="true"><i className="vdots" /></div>
          <MyRow beyond={Math.max(0, (first.meRow ?? 0) - rows.length - 1)} toPlace={toMe}>{row(first.me)}</MyRow></>
      )}
    </div>
  )
}

/** Where a pinned row would sit without sticking: right under the element before it. */
function placeOf(pin: HTMLElement): number {
  const prev = pin.previousElementSibling
  return prev ? prev.getBoundingClientRect().bottom : pin.parentElement!.getBoundingClientRect().top
}

/** Scrolls the page so a pinned row's own place sits mid-screen. */
function scrollToPlace(pin: HTMLElement) {
  window.scrollBy({ top: placeOf(pin) + pin.offsetHeight / 2 - innerHeight / 2, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' })
}

/**
 * Your row stays on screen: sticky at both ends. While it's held away from its place, a pill on the
 * side facing the list says which way your place is and how far ("↓ 21 rows down"), and tapping the
 * pill or the row takes you there. `beyond` counts rows not loaded yet between the list and your
 * row; then `toPlace` loads them and scrolls instead.
 */
function MyRow({ children, beyond = 0, toPlace }: { children: ReactNode; beyond?: number; toPlace?: () => Promise<void> }) {
  const ref = useRef<HTMLDivElement>(null)
  const [held, setHeld] = useState<'below' | 'above' | null>(null)
  const [away, setAway] = useState(0)
  useEffect(() => {
    const pin = ref.current
    const list = pin?.parentElement
    if (!pin || !list) return
    let frame = 0
    const update = () => {
      frame = 0
      // Where the row would sit without sticking: right under the element before it.
      const d = pin.getBoundingClientRect().top - placeOf(pin)
      setHeld(d < -0.5 ? 'below' : d > 0.5 ? 'above' : null)
      // How many rows lie between: the distance in the height of an ordinary row.
      setAway(Math.max(1, Math.round(Math.abs(d) / (list.firstElementChild?.getBoundingClientRect().height || 53))))
      pin.style.setProperty('--o', Math.min(1, Math.abs(d) / 44).toFixed(3))
    }
    const queue = () => { if (!frame) frame = requestAnimationFrame(update) }
    const resized = new ResizeObserver(queue)
    resized.observe(list)
    addEventListener('scroll', queue, { passive: true, capture: true })
    addEventListener('resize', queue)
    update()
    return () => { cancelAnimationFrame(frame); resized.disconnect(); removeEventListener('scroll', queue, { capture: true }); removeEventListener('resize', queue) }
  }, [])
  const jump = () => {
    if (!ref.current || !held) return
    if (toPlace) void toPlace()
    else scrollToPlace(ref.current)
  }
  const rowsAway = away + (held === 'below' ? beyond : 0)
  const label = held && `${fmt(rowsAway)} row${rowsAway === 1 ? '' : 's'} ${held === 'below' ? 'down' : 'up'}`
  return (
    <div ref={ref} className={`lb-pin${held ? ` ${held}` : ''}`} onClickCapture={held ? (ev) => ev.preventDefault() : undefined} onClick={held ? jump : undefined}>
      <div className="band" />
      {held && (
        <button type="button" className="pin-dir" aria-label={`Your place is ${label}. Jump to it.`} onClick={(e) => { e.stopPropagation(); jump() }}>
          <span aria-hidden="true">{held === 'below' ? '↓' : '↑'}</span>{label}
        </button>
      )}
      {children}
    </div>
  )
}

/** A player's row; a member's opens their profile (guests have none). Memoised: another page of rows leaves these alone. */
const Row = memo(function Row({ e }: { e: LeaderboardEntry }) {
  const cells = (
    <>
      <span className="pos">{e.position}</span>
      <Avatar id={e.playerId} name={e.displayName} src={e.avatar} />
      <div className="who"><div className="named"><b>{e.isMe ? `You · ${e.displayName}` : e.displayName}</b><NameBadge badge={e.badge} /></div>{e.rankName && <span>{e.rankName}</span>}</div>
      <span className="pts">{fmt(e.total)}</span>
    </>
  )
  return e.profile
    ? <Link className={`lb${e.isMe ? ' me' : ''}`} to={`/p/${e.profile}`}>{cells}</Link>
    : <div className={`lb${e.isMe ? ' me' : ''}`}>{cells}</div>
})

function SlowBurnBoardView() {
  const [params, setParams] = useSearchParams()
  const scope: SlowBurnScope = params.get('scope') === 'ever' ? 'ever' : 'today'
  const today = useSlowBurn('today').data
  const lb = useSlowBurnBoard(today?.number ?? null, scope)
  const board = lb.data
  const set = (s: SlowBurnScope) => setParams((p) => { const q = new URLSearchParams(p); if (s === 'ever') q.set('scope', 'ever'); else q.delete('scope'); return q })

  return (
    <>
      <div className="lb-scope">
        <div className="seg" role="tablist">
          <button type="button" role="tab" aria-selected={scope === 'today'} className={scope === 'today' ? 'on' : ''} onClick={() => set('today')}>Today</button>
          <button type="button" role="tab" aria-selected={scope === 'ever'} className={scope === 'ever' ? 'on' : ''} onClick={() => set('ever')}>Best ever</button>
        </div>
      </div>
      {lb.error ? <ErrorNote error={lb.error} retry={() => lb.reload()} /> : !board ? <Loading label="Measuring the runs…" /> : (
        <>
          <div className="card lb-summary">
            <div className="lb-sum-head">
              <b>{scope === 'today' ? `No. ${board.edition}` : 'Every run'} · {fmt(board.players)} {scope === 'today' ? 'run' : 'player'}{board.players === 1 ? '' : 's'}</b>
              <span className="eyebrow">{board.best !== null ? `best ${fmt(board.best)} · ` : ''}by points</span>
            </div>
            {scope === 'today' && board.players > 0 && <DepthHistogram bins={board.histogram} you={board.me?.depth ?? null} height={38} />}
          </div>
          {board.entries.length === 0 ? (
            <p className="muted empty-note">Nobody has finished a run yet. Be the first on the board.</p>
          ) : (
            <BoardList first={board} load={(offset, limit) => api.get<SlowBurnBoard>(`/slow-burn/${board.edition}/board?scope=${scope}&offset=${offset}&limit=${limit}`)} row={(e) => <RunRow e={e} scope={scope} />} />
          )}
        </>
      )}
    </>
  )
}

const RunRow = memo(function RunRow({ e, scope }: { e: SlowBurnEntry; scope: SlowBurnScope }) {
  const how = { burned_out: 'burned out', put_to_bed: 'ended early', fuse_out: 'fuse ran out', day_over: 'day ended' }[e.ended]
  return (
    <div className={`lb sb-lb${e.isMe ? ' me' : ''}`}>
      <span className="pos">{e.position}</span>
      <Avatar id={e.playerId} name={e.displayName} src={e.avatar} />
      <div className="who"><b>{e.isMe ? `You · ${e.displayName}` : e.displayName}</b><span>{scope === 'ever' ? `No. ${e.edition} · ${how}` : how}</span></div>
      <div className="depth"><b>{fmt(e.points)}</b><span>row {e.depth}</span></div>
    </div>
  )
})
